import React from 'react';

/**
 * Titre en deux parties (début neutre + fin mise en couleur).
 *
 * Le séparateur ne peut pas être écrit en dur dans le JSX : il dépend de la langue.
 * En chinois deux segments Han se collent (學習中心), mais une espace reste correcte
 * devant du texte latin ; en allemand un titre coupé sur un trait d'union
 * (« Video- » + « Tutorials ») doit rester soudé, tout comme une élision
 * française (« Générateur d' » + « Horizon »).
 * D'où cette règle unique, appliquée partout via ce composant.
 */

// Un caractère Han (idéogramme) en fin/début de segment : jonction sans espace.
const HAN_END = /\p{Script=Han}$/u;
const HAN_START = /^\p{Script=Han}/u;

// Mot coupé : trait d'union (« Video- ») ou élision (« Générateur d' »).
const GLUING_END = /[-'’]$/;

/**
 * Faut-il une espace entre les deux parties ?
 * Non si l'une est vide, si le titre se termine sur un mot coupé,
 * ou si la jonction est idéographique (Han + Han).
 */
export const needsTitleSeparator = (left: string, right: string): boolean => {
    if (!left || !right) return false;
    if (GLUING_END.test(left)) return false;
    return !(HAN_END.test(left) && HAN_START.test(right));
};

/** Assemble les deux parties d'un titre avec le séparateur typographique correct. */
export const joinTitleParts = (title: string, highlight: string): string => {
    const left = (title || '').trim();
    const right = (highlight || '').trim();
    return needsTitleSeparator(left, right) ? `${left} ${right}` : left + right;
};

interface SplitTitleProps {
    title: string;
    highlight: string;
    /** Classes de la partie mise en valeur (couleur d'accent selon la page). */
    highlightClassName?: string;
}

export const SplitTitle: React.FC<SplitTitleProps> = ({ title, highlight, highlightClassName = 'text-primary' }) => {
    const left = (title || '').trim();
    const right = (highlight || '').trim();

    return (
        <>
            {left}
            {needsTitleSeparator(left, right) && ' '}
            {right && <span className={highlightClassName}>{right}</span>}
        </>
    );
};
